import { ChangeEvent, MouseEvent } from 'react';
import { FieldConfig, FieldProps, FieldType } from './fields.types';
import { ValuesState } from './useForm.types';

export const DEFAULT_STATE: Record<FieldType, any> = {
  [FieldType.Text]: '',
  [FieldType.Date]: '',
  [FieldType.Checkbox]: false,
  [FieldType.Radio]: undefined,
  [FieldType.Dropdown]: undefined,
  [FieldType.Autocomplete]: undefined,
  [FieldType.Multiselect]: [],
  [FieldType.Number]: '',
  [FieldType.WithoutWildcards]: '',
  [FieldType.DecimalNumber]: ''
};

export type FieldPropsCreator<TFieldType extends FieldType> = <
  TValues extends ValuesState
>(params: {
  fieldName: string;
  field: FieldConfig<TFieldType>;
  values: TValues;
  setAsTouched: (fieldName: string) => void;
  checkFieldStatuses: (fieldName: string, value: any) => void;
  setValues: React.Dispatch<React.SetStateAction<TValues>>;
  setFocused: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => FieldProps<TFieldType>;

const makeTextFieldProps: FieldPropsCreator<FieldType.Text> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    checkFieldStatuses(fieldName, event.target.value);
    setValues((values) => ({ ...values, [fieldName]: event.target.value }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };
  return {
    name: fieldName,
    value: values[fieldName],
    onChange,
    onBlur,
    placeholder: field.placeholder
  };
};
const makeNumberFieldProps: FieldPropsCreator<FieldType.Number> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    checkFieldStatuses(fieldName, event.target.value.replace(/[^\d]/g, ''));
    setValues((values) => ({
      ...values,
      [fieldName]: event.target.value.replace(/[^\d]/g, '')
    }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };
  return {
    name: fieldName,
    value: values[fieldName],
    onChange,
    onBlur,
    placeholder: field.placeholder
  };
};

const makeWithoutWildcardsFieldProps: FieldPropsCreator<FieldType.WithoutWildcards> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    checkFieldStatuses(fieldName, event.target.value.replace(/[^\w]/g, ''));
    setValues((values) => ({ ...values, [fieldName]: event.target.value.replace(/[^\w]/g, '') }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };
  return {
    name: fieldName,
    value: values[fieldName],
    onChange,
    onBlur,
    placeholder: field.placeholder
  };
};

const makeDateFieldProps: FieldPropsCreator<FieldType.Date> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (event: string) => {
    checkFieldStatuses(fieldName, event);
    setValues((values) => ({ ...values, [fieldName]: event }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };
  return {
    name: fieldName,
    value: values[fieldName],
    onChange,
    onBlur,
    htmlType: 'date',
    placeholder: field.placeholder
  };
};

const makeCheckboxFieldProps: FieldPropsCreator<FieldType.Checkbox> = ({
  fieldName,
  field,
  values,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    checkFieldStatuses(fieldName, event.target.checked);
    setValues((values) => ({ ...values, [fieldName]: event.target.checked }));
  };
  return {
    name: fieldName,
    checked: values[fieldName],
    label: field.label,
    onChange
  };
};

const makeRadioFieldProps: FieldPropsCreator<FieldType.Radio> = ({
  fieldName,
  field,
  checkFieldStatuses,
  values,
  setValues
}) => {
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    // @ts-ignore value существует, т.к. клик обрабатывается на контейнер, содержащий радио инпут
    if (event.target.value) {
      // @ts-ignore
      checkFieldStatuses(fieldName, event.target.value);
      // @ts-ignore
      setValues((values) => ({ ...values, [fieldName]: event.target.value }));
    }
  };
  return {
    options: field.options,
    name: fieldName,
    value: values[fieldName],
    onClick
  };
};

const makeDropdownFieldProps: FieldPropsCreator<FieldType.Dropdown> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (value?: string) => {
    checkFieldStatuses(fieldName, value);
    setValues((values) => ({ ...values, [fieldName]: value }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };

  return {
    label: field.label,
    name: fieldName,
    value: values[fieldName],
    options: field.options,
    placeholder: field.placeholder,
    onChange,
    onBlur
  };
};

const makeAutocompleteFieldProps: FieldPropsCreator<FieldType.Autocomplete> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues,
  setFocused
}) => {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    checkFieldStatuses(fieldName, value);
    setValues((values) => ({ ...values, [fieldName]: value }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
    setFocused(undefined);
  };
  const onFocus = () => {
    setFocused(fieldName);
  };

  return {
    name: fieldName,
    value: values[fieldName],
    onChange,
    onBlur,
    onFocus,
    placeholder: field.placeholder,
    options: field.options
  };
};

const makeMultiselectFieldProps: FieldPropsCreator<FieldType.Multiselect> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (selectedValues: string[]) => {
    checkFieldStatuses(fieldName, selectedValues);
    setValues((values) => ({ ...values, [fieldName]: selectedValues }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };
  return {
    name: fieldName,
    values: values[fieldName],
    onChange,
    onBlur,
    placeholder: field.placeholder,
    options: field.options
  };
};

const makeDecimalNumberFieldProps: FieldPropsCreator<FieldType.DecimalNumber> = ({
  fieldName,
  field,
  values,
  setAsTouched,
  checkFieldStatuses,
  setValues
}) => {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    checkFieldStatuses(
      fieldName,
      event.target.value.match(/^(?!0\d)(?!\.)\d*(\.\d{0,2})?/g)?.join('') || ''
    );
    setValues((values) => ({
      ...values,
      [fieldName]: event.target.value.match(/^(?!0\d)(?!\.)\d*(\.\d{0,2})?/g)?.join('') || ''
    }));
  };
  const onBlur = () => {
    setAsTouched(fieldName);
  };
  return {
    name: fieldName,
    value: values[fieldName],
    onChange,
    onBlur,
    placeholder: field.placeholder
  };
};

export const getFieldProps = {
  [FieldType.Text]: makeTextFieldProps,
  [FieldType.Date]: makeDateFieldProps,
  [FieldType.Checkbox]: makeCheckboxFieldProps,
  [FieldType.Radio]: makeRadioFieldProps,
  [FieldType.Dropdown]: makeDropdownFieldProps,
  [FieldType.Autocomplete]: makeAutocompleteFieldProps,
  [FieldType.Multiselect]: makeMultiselectFieldProps,
  [FieldType.Number]: makeNumberFieldProps,
  [FieldType.WithoutWildcards]: makeWithoutWildcardsFieldProps,
  [FieldType.DecimalNumber]: makeDecimalNumberFieldProps
};

export type PropsMaker<T extends FieldType> = T extends FieldType.Text
  ? FieldPropsCreator<FieldType.Text>
  : T extends FieldType.Date
    ? FieldPropsCreator<FieldType.Date>
    : T extends FieldType.Autocomplete
      ? FieldPropsCreator<FieldType.Autocomplete>
      : T extends FieldType.Checkbox
        ? FieldPropsCreator<FieldType.Checkbox>
        : T extends FieldType.Dropdown
          ? FieldPropsCreator<FieldType.Dropdown>
          : T extends FieldType.Radio
            ? FieldPropsCreator<FieldType.Radio>
            : T extends FieldType.Multiselect
              ? FieldPropsCreator<FieldType.Multiselect>
              : T extends FieldType.Number
                ? FieldPropsCreator<FieldType.Number>
                : T extends FieldType.WithoutWildcards
                  ? FieldPropsCreator<FieldType.WithoutWildcards>
                  : T extends FieldType.DecimalNumber
                    ? FieldPropsCreator<FieldType.DecimalNumber>
                    : never;
